import { createFeature, createReducer, on } from '@ngrx/store';
import { DocsActions } from './docs.actions';
import { docsAdapter, DocsState, initialDocsState } from './docs.models';
import { docKey } from './docs.normalize';

function withStatus(state: DocsState, k: string, status: DocsState['statusByKey'][string], error: string | null = null): DocsState {
  return {
    ...state,
    statusByKey: { ...state.statusByKey, [k]: status },
    errorByKey: { ...state.errorByKey, [k]: error },
  };
}

export const docsFeature = createFeature({
  name: 'docs',
  reducer: createReducer(
    initialDocsState,
    // NOTE: loadDoc intentionally has NO handler — it must not change status, so
    // the load effect's guard can read the pre-load status. loadDocStarted (emitted
    // by the effect) is what flips the key to 'loading'.
    on(DocsActions.loadDocStarted, (state, { projectId, key }) => withStatus(state, docKey(projectId, key), 'loading')),
    on(DocsActions.saveDoc, (state, { projectId, key }) => withStatus(state, docKey(projectId, key), 'saving')),

    on(DocsActions.loadDocSucceeded, (state, { projectId, key, doc }): DocsState => {
      const k = docKey(projectId, key);
      if (!doc || !doc.id) return withStatus(state, k, 'loaded');
      return {
        ...withStatus(state, k, 'loaded'),
        entities: docsAdapter.upsertOne(doc, state.entities),
        keyIndex: { ...state.keyIndex, [k]: doc.id },
      };
    }),

    on(DocsActions.saveDocSucceeded, (state, { projectId, key, doc }): DocsState => {
      const k = docKey(projectId, key);
      const prevId = state.keyIndex[k];
      // Drop the synthetic optimistic placeholder once the real server doc lands.
      const baseEntities =
        prevId && doc.id && prevId !== doc.id && prevId.startsWith('local:')
          ? docsAdapter.removeOne(prevId, state.entities)
          : state.entities;
      return {
        ...withStatus(state, k, 'loaded'),
        entities: docsAdapter.upsertOne(doc, baseEntities),
        keyIndex: { ...state.keyIndex, [k]: doc.id ?? state.keyIndex[k] },
      };
    }),

    on(DocsActions.applyLocalData, (state, { projectId, key, data }): DocsState => {
      const k = docKey(projectId, key);
      const existingId = state.keyIndex[k];
      const id = existingId ?? `local:${k}`;
      const prev = existingId ? state.entities.entities[existingId] : undefined;
      const nextDoc = { ...(prev ?? ({} as any)), id, projectId, key, data, version: prev?.version ?? 0 } as any;
      return {
        ...withStatus(state, k, 'loaded'),
        entities: docsAdapter.upsertOne(nextDoc, state.entities),
        keyIndex: { ...state.keyIndex, [k]: id },
      };
    }),

    on(DocsActions.loadDocFailed, (state, { projectId, key, error }) => withStatus(state, docKey(projectId, key), 'error', error)),
    on(DocsActions.saveDocFailed, (state, { projectId, key, error }) => withStatus(state, docKey(projectId, key), 'error', error)),

    on(DocsActions.clearProject, (state, { projectId }): DocsState => {
      const prefix = `${projectId}::`;
      const keyIndex = { ...state.keyIndex };
      const statusByKey = { ...state.statusByKey };
      const errorByKey = { ...state.errorByKey };
      const removeIds: string[] = [];
      for (const k of Object.keys(keyIndex)) {
        if (k.startsWith(prefix)) { removeIds.push(keyIndex[k]); delete keyIndex[k]; delete statusByKey[k]; delete errorByKey[k]; }
      }
      return { ...state, keyIndex, statusByKey, errorByKey, entities: docsAdapter.removeMany(removeIds, state.entities) };
    })
  ),
});
